/**
 * "A condição comercial desta UC mudou neste salvamento?" — a pergunta que
 * decide se a régua de aprovação de desconto deve rodar.
 *
 * Solicitação 5916: o cadastro de UC chamava
 * `checkAndRequestDiscountApproval` sem informar carência, então pedido de
 * carência feito por ali escapava da aprovação do gerente.
 *
 * ⚠️ O DESCONTO ficou deliberadamente de fora deste conserto (decisão do dono do
 * produto, 21/09/2026): a avaliação dele segue exatamente como sempre foi. A
 * tela também poderia informar o `planoCodigo` da negociação, o que tornaria a
 * régua mais severa sobre o desconto — mas desconto é dinheiro, e uma condição
 * que o vendedor fecha sozinho hoje passaria a travar esperando gerente. Só
 * EXIGE permissão o que muda e precisa de permissão.
 *
 * Esta guarda existe porque a chamada rodava em TODO salvamento da UC, não só
 * quando a condição mudava, e 41,5% das UCs têm carência de verdade (amostra de
 * 1.000, quase toda "até 90 dias"). Sem ela, corrigir um CEP viraria pedido de
 * aprovação em 4 de cada 10 edições, com ninguém tendo tocado em nada — e o
 * gerente perderia o pedido real no meio da enxurrada.
 *
 * ⚠️ Esta função diz que a CONDIÇÃO mudou; ela não diz o QUE mudou. Quem chama
 * decide o que enviar à régua: a carência só vai quando ela própria mudou, para
 * carência preexistente (já aprovada quando foi definida) não ser cobrada de
 * novo a cada ajuste de cadastro.
 *
 * É a mesma proteção que o recálculo de condição comercial já tinha na mesma
 * tela ("só reclassifica quando o CONSUMO mudou", depois de corrigir um CEP ter
 * reescrito o multiplicador que é base de comissão). A régua de desconto tinha
 * ficado de fora.
 *
 * Módulo sem I/O de propósito: é decisão de negócio, e decisão de negócio no
 * meio de um handler de formulário não tem teste.
 */

export interface CondicaoComercial {
    /** Percentual, na escala da TELA (15 = 15%), não a fração do banco (0.15). */
    descontoTarifa?: number | string | null;
    descontoBandeira?: number | string | null;
    carencia?: string | null;
}

/** Percentual com 2 casas, vindo da fração que o banco guarda (0.155 → 15.5). */
export function fracaoParaPercentual(v: unknown): number {
    return Math.round((Number(v) || 0) * 10000) / 100;
}

const numero = (v: unknown): number => Number(v) || 0;
const texto = (v: unknown): string => String(v ?? '').trim();

/**
 * @param anterior estado gravado, ou `null` quando a UC está sendo criada.
 * @param atual    o que o formulário vai gravar.
 *
 * ⚠️ UC nova (anterior `null`) SEMPRE conta como mudança: não há "antes" para
 * comparar, e é exatamente no nascimento que a condição comercial é definida.
 * Tratar como "não mudou" deixaria passar sem régua justamente o caso que mais
 * importa.
 */
export function condicaoComercialMudou(
    anterior: CondicaoComercial | null | undefined,
    atual: CondicaoComercial,
): boolean {
    if (!anterior) return true;

    return numero(anterior.descontoTarifa) !== numero(atual.descontoTarifa)
        || numero(anterior.descontoBandeira) !== numero(atual.descontoBandeira)
        || texto(anterior.carencia) !== texto(atual.carencia);
}
